<template>
    <q-dialog
        v-model="isOpen"
        content-class="add-employee"
        @input="toggleDialog"
    >
        <q-card class="q-pa-md">
            <q-card-section>
                <h4>{{ $t('profile.add_employee') }}</h4>
            </q-card-section>
            <q-card-section>
                <div
                    v-for="(email, index) in emails"
                    :key="index"
                    class="list q-mb-lg"
                >
                    <div class="row q-mt-md">
                        <div class="col-auto">
                            <q-btn
                                icon="remove"
                                class="q-mr-sm q-my-sm"
                                size="sm"
                                round
                                dense
                                :disable="email.isLoading"
                                :ripple="false"
                                @click="removeEmail(index)"
                            >
                                <q-tooltip
                                    anchor="top middle"
                                    self="center middle"
                                >
                                    {{ $t('actions.remove') }}
                                </q-tooltip>
                            </q-btn>
                        </div>
                        <q-input
                            :ref="`email${index}`"
                            v-model="email.student_email"
                            class="col q-pb-none"
                            bg-color="white"
                            type="text"
                            dense
                            outlined
                            :placeholder="$t('general.email')"
                            :disable="email.isSent || email.isLoading"
                            :hide-details="email.isSent"
                            :error-message="emailErrors(index)"
                            :error="emailErrors(index) != ''"
                            @input="
                                $v.emails.$each[index]['student_email'].$touch()
                            "
                            @blur="
                                $v.emails.$each[index]['student_email'].$touch()
                            "
                        />
                        <q-icon
                            v-if="email.isError"
                            class="q-ml-sm"
                            name="close"
                            color="negative"
                            size="lg"
                        ></q-icon>
                        <q-icon
                            v-else-if="email.isSent"
                            class="q-ml-sm"
                            name="check"
                            color="positive"
                            size="lg"
                        ></q-icon>
                        <q-spinner-pie
                            v-else-if="email.isLoading"
                            class="q-ml-sm"
                            color="primary"
                            size="36px"
                        />
                    </div>
                    <div v-if="email.isError" class="row justify-end">
                        <span class="q-pr-xl text-red">
                            {{ $t('notify.invite_was_not_sent') }}
                            {{ $t(email.message) }}
                        </span>
                    </div>
                    <div v-if="email.isSent" class="row justify-end">
                        <span class="q-pr-xl text-positive">{{
                            $t('notify.invite_was_sent')
                        }}</span>
                    </div>
                </div>
                <div class="row q-mt-sm">
                    <q-btn
                        class="btn-custom"
                        color="secondary"
                        flat
                        dense
                        :ripple="false"
                        @click="addEmail"
                    >
                        <q-icon name="add" size="20px" class="q-mr-sm" />
                        <span>{{ $t('profile.add_email') }}</span>
                    </q-btn>
                </div>
            </q-card-section>
            <q-card-actions align="right">
                <q-btn flat :ripple="false" @click="toggleDialog">
                    <span>{{ $t('actions.cancel') }}</span>
                </q-btn>
                <q-btn
                    color="secondary"
                    :ripple="false"
                    :disabled="sendButtonDisabled || isSendingEmails"
                    @click="sendInvite"
                >
                    <span>{{ $t('actions.send_invite') }}</span>
                </q-btn>
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<script>
import affiliationsService from '@/services/modules/profile/affiliations'
import notify from '@/local-shared/mixins/notify'
import { email, required } from 'vuelidate/lib/validators'
import store from '@/store'
import axios from 'axios'
import _ from 'lodash'

const loadingKeys = {
    sendingEmails: 'sending-emails',
}

export default {
    name: 'AddEmployee',

    mixins: [notify],

    props: {
        show: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            isOpen: false,

            emails: [
                {
                    student_email: '',
                },
            ],

            requests: [],
        }
    },

    validations: {
        emails: {
            $each: {
                isUnique(currItem, itemArr) {
                    let found = _.filter(itemArr, function (elem) {
                        return elem.student_email === currItem.student_email
                    })
                    return found.length <= 1 // validation occurs only after object is added to array, condition could be changed to == 1
                },
                student_email: {
                    required,
                    email, // for array of objects
                },
            },
        },
    },

    computed: {
        isSendingEmails() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.sendingEmails
            ).length
        },

        sendButtonDisabled() {
            const filtered = _.filter(this.emails, function (elem) {
                return !elem.isSent && !elem.isError && !elem.isLoading
            })

            return (
                this.emails.length === 0 ||
                this.$v.emails.$invalid ||
                filtered.length === 0
            )
        },

        user() {
            return store.getters['session/user']
        },
    },

    watch: {
        show(value) {
            this.isOpen = value

            if (this.isOpen) {
                let l = this.emails.length

                this.$nextTick(() => {
                    this.$refs[`email${l - 1}`][0].focus()
                })
            }
        },
    },

    methods: {
        toggleDialog() {
            this.$emit('toggle-dialog', !this.isOpen)
        },

        addEmail() {
            let l = this.emails.push({
                isError: false,
                isLoading: false,
                isSent: false,
                student_email: '',
                message: '',
            })

            this.$nextTick(() => {
                this.$refs[`email${l - 1}`][0].focus()
            })
        },

        removeEmail(index) {
            this.emails.splice(index, 1)
        },

        emailErrors(index) {
            let errors = ''

            if (!this.$v.emails.$each[index].student_email.$dirty) {
                return errors
            }

            if (!this.$v.emails.$each[index].student_email.required) {
                errors = errors.concat(this.$t('validations.email_is_required'))
            }

            if (!this.$v.emails.$each[index].student_email.email) {
                errors = errors.concat(
                    this.$t('validations.email_has_invalid_format')
                )
            }

            if (!this.$v.emails.$each[index].isUnique) {
                errors = errors.concat(
                    this.$t('validations.email_is_not_unique')
                )
            }

            return errors
        },

        async sendInvite() {
            this.$v.emails.$touch

            if (this.$v.emails.$invalid) {
                return
            }

            this.loadingAdd(loadingKeys.sendingEmails)

            let self = this
            this.emails.forEach((email) => {
                if (!email.isSent && !email.isError && !email.isLoading) {
                    let parameters = {
                        data: {
                            client_id: self.user.affiliations[0].client_id,
                            sender_id: self.user.id,
                            recipient_email: email.student_email,
                        },
                    }
                    email.isLoading = true
                    email.message = ''
                    self.requests.push(
                        affiliationsService.requests.create(parameters)
                    )
                }
            })

            axios
                .all(self.requests)
                .then((responses) => {
                    responses.forEach((response) => {
                        let data = JSON.parse(response.config.data).data
                        let userEmail = data.recipient_email
                        let emailIndex = self.emails.findIndex(
                            (email) => email.student_email === userEmail
                        )

                        if (emailIndex != -1) {
                            if (response.status === 200) {
                                if (response.data.data[0].id === 0) {
                                    // self.emails[emailIndex].isError = true
                                    this.$set(
                                        self.emails[emailIndex],
                                        'isError',
                                        true
                                    )
                                } else {
                                    // self.emails[emailIndex].isSent = true
                                    this.$set(
                                        self.emails[emailIndex],
                                        'isSent',
                                        true
                                    )
                                }
                                self.emails[emailIndex].message =
                                    response.data.data[0].message
                            } else {
                                // self.emails[emailIndex].isError = true
                                this.$set(
                                    self.emails[emailIndex],
                                    'isError',
                                    true
                                )
                            }

                            self.emails[emailIndex].isLoading = false
                        }
                    })
                    this.$forceUpdate()
                    this.loadingRemove(loadingKeys.sendingEmails)
                })
                .catch((errors) => {
                    console.log(errors) //TODO: remove errors and let interceptors do the job OR display erros with notify dialog
                    this.loadingRemove(loadingKeys.sendingEmails)
                })

            self.requests = []
            this.loadingRemove(loadingKeys.sendingEmails)
        },
    },
}
</script>
